import { useEffect } from "react";
import type { IntentType, ContentType, HeroAsinWithIntent, ABTestType, AxisFingerprint } from "@/types/ab-tests";
import { emptyStructureTreatments } from "@/components/ab-tests/StructurePanel";
import { readSessionDraft } from "@/components/ab-tests/session-draft";
import type { ScorecardPrefill, SignalPrefill, KeywordPrefill, ScorecardGapSnapshot } from "@/components/ab-tests/create-test-modal.types";
import type { ABTest } from "@/types/ab-tests";
import { detectScorecardFields, detectScorecardFieldsFromStrategyField } from "@/utils/detect-scorecard-fields";

const KEYWORDS_PAGE_SIZE = 50;

interface UseTestModalInitParams {
  open: boolean;
  heroAsin: HeroAsinWithIntent;
  parsedProductId: string;
  selectedBrandId: number | null | undefined;
  initialTestType?: ABTestType;
  initialTargetPhrase?: string;
  prefillFromDraft?: ABTest | null;
  prefillFromScorecard?: ScorecardPrefill | null;
  prefillFromSignal?: SignalPrefill | null;
  prefillFromKeyword?: KeywordPrefill | null;
  /** AUT-430 (WEB-008): the strategy link the page opened with (FR-15). */
  initialStrategyLinkId?: number | null;
  detectFingerprintMutation: { mutateAsync: (args: { productId: number; brandId: number }) => Promise<{ fingerprint: AxisFingerprint; summary: string }> };
  extractProductNameMutation: { mutateAsync: (args: { productId: number; brandId: number }) => Promise<unknown> };
  setVisibleKeywordCount: (n: number) => void;
  setHypothesisValidation: (v: null) => void;
  setCopyValidation: (v: null) => void;
  setIsSaving: (v: boolean) => void;
  setStructureTreatments: (v: ReturnType<typeof emptyStructureTreatments>) => void;
  setUndoStack: (v: []) => void;
  setRedoStack: (v: []) => void;
  setShowSuccess: (v: boolean) => void;
  setSuccessPayload: (v: null) => void;
  setParsedZonesA: (v: Record<string, null>) => void;
  setVariantBFieldSources: (v: Record<string, never>) => void;
  setFingerprintData: (v: { fingerprint: AxisFingerprint; summary: string } | null) => void;
  setFingerprintTarget: (v: Record<string, number>) => void;
  setIsFingerprintLoading: (v: boolean) => void;
  setVariantATitle: (v: string) => void;
  setVariantABullets: (v: Record<string, string>) => void;
  setStep: (v: 1 | 2) => void;
  setSelectedFields: (v: ContentType[]) => void;
  setTargetPhrase: (v: string) => void;
  setStrategyLinkId: (v: number | null | undefined) => void;
  /**
   * AUT-430 (WEB-008): the target phrase's value when the page opened, held
   * by `CreateTestModal` and written only here, in every branch that calls
   * `setTargetPhrase` -- Reset reads it back (FR-15 / H2). A ref, not state:
   * it must not itself trigger a render or feed the session-draft effect.
   */
  openTargetPhraseRef: React.MutableRefObject<string>;
  setIntentFrom: (v: IntentType) => void;
  setIntentTo: (v: IntentType | "") => void;
  setHypothesis: (v: string) => void;
  setVariantBTitle: (v: string) => void;
  setVariantBBullets: (v: Record<string, string>) => void;
  setVariantBMode: (v: "use-ai" | "starting-point" | "manual-editor") => void;
  setLastSource: (v: string | null) => void;
  setLastSourceDetail: (v: string) => void;
  setAppliedScorecardGaps: React.Dispatch<React.SetStateAction<string[]>>;
  setGapSnapshots: React.Dispatch<React.SetStateAction<Record<string, ScorecardGapSnapshot>>>;
  skipPersistRef: React.MutableRefObject<boolean>;
}

function initFingerprintTarget(fingerprint: AxisFingerprint): Record<string, number> {
  const axisKeys = ["benefit", "audience", "use-case", "ingredient", "format"] as const;
  const target: Record<string, number> = {};
  for (const axis of axisKeys) {
    const layer = fingerprint[axis];
    target[axis] = Math.max(0, Math.min(100, Math.round(layer.locked + layer.descriptor)));
  }
  return target;
}

export function useTestModalInit({
  open, heroAsin, parsedProductId, selectedBrandId,
  initialTestType, initialTargetPhrase, initialStrategyLinkId,
  prefillFromDraft, prefillFromScorecard, prefillFromSignal, prefillFromKeyword,
  detectFingerprintMutation, extractProductNameMutation,
  setVisibleKeywordCount, setHypothesisValidation, setCopyValidation, setIsSaving,
  setStructureTreatments, setUndoStack, setRedoStack, setShowSuccess, setSuccessPayload,
  setParsedZonesA, setVariantBFieldSources, setFingerprintData, setFingerprintTarget, setIsFingerprintLoading,
  setVariantATitle, setVariantABullets, setStep, setSelectedFields, setTargetPhrase, setStrategyLinkId, openTargetPhraseRef,
  setIntentFrom, setIntentTo, setHypothesis, setVariantBTitle, setVariantBBullets,
  setVariantBMode, setLastSource, setLastSourceDetail, setAppliedScorecardGaps, setGapSnapshots, skipPersistRef,
}: UseTestModalInitParams) {
  useEffect(() => {
    if (!open) return;
    setVisibleKeywordCount(KEYWORDS_PAGE_SIZE);
    setHypothesisValidation(null); setCopyValidation(null); setIsSaving(false); setAppliedScorecardGaps([]); setGapSnapshots({});
    setStructureTreatments(emptyStructureTreatments());
    setUndoStack([]); setRedoStack([]); setShowSuccess(false); setSuccessPayload(null);
    setParsedZonesA({}); setVariantBFieldSources({});
    setFingerprintData(null); setFingerprintTarget({ benefit: 0, audience: 0, "use-case": 0, ingredient: 0, format: 0 }); setIsFingerprintLoading(false);

    const numericId = Number(parsedProductId);
    if (Number.isFinite(numericId) && numericId > 0 && selectedBrandId) {
      const detectFp = () => {
        void detectFingerprintMutation.mutateAsync({ productId: numericId, brandId: selectedBrandId })
          .then(res => { setFingerprintData(res); setFingerprintTarget(initFingerprintTarget(res.fingerprint)); })
          .catch(() => {});
      };
      void extractProductNameMutation.mutateAsync({ productId: numericId, brandId: selectedBrandId }).then(detectFp).catch(detectFp);
    }

    setVariantATitle(heroAsin.title);
    const aB: Record<string, string> = {};
    heroAsin.bulletPoints.forEach((bp, idx) => { aB[`bullet-${idx + 1}`] = bp; });
    setVariantABullets(aB);

    if (prefillFromDraft) {
      const d = prefillFromDraft;
      setStep(2); setSelectedFields(d.scopeFields ?? [d.contentType]);
      openTargetPhraseRef.current = d.targetKeyword ?? d.targetCluster ?? "";
      setTargetPhrase(d.targetKeyword ?? d.targetCluster ?? "");
      setStrategyLinkId(initialStrategyLinkId);
      setIntentFrom(d.intentShift.from); setIntentTo(d.intentShift.to || "");
      setHypothesis(d.hypothesis); setVariantBTitle(d.variantB.title ?? "");
      const bB: Record<string, string> = {};
      (d.scopeFields ?? [d.contentType]).filter(f => f.startsWith("bullet-")).forEach((f, i) => { if (d.variantB.bullets?.[i]) bB[f] = d.variantB.bullets[i]; });
      setVariantBBullets(bB); setVariantBMode("use-ai"); setLastSource(d.source ?? null); setLastSourceDetail(d.sourceDetail ?? "");
      if (d.source === "scorecard" && d.sourceDetail) {
        try {
          const gaps = JSON.parse(d.sourceDetail);
          if (Array.isArray(gaps)) setAppliedScorecardGaps(gaps as string[]);
          else setAppliedScorecardGaps([d.sourceDetail]);
        } catch { setAppliedScorecardGaps([d.sourceDetail]); }
      } else {
        setAppliedScorecardGaps([]);
      }
      return;
    }

    const mkBB = () => { const b: Record<string, string> = {}; heroAsin.bulletPoints.forEach((bp, i) => { b[`bullet-${i + 1}`] = bp; }); return b; };

    if (prefillFromScorecard) {
      skipPersistRef.current = true;
      const scorecardFields = prefillFromScorecard.field
        ? detectScorecardFieldsFromStrategyField(prefillFromScorecard.field, prefillFromScorecard.recommendation, heroAsin.bulletPoints.length)
        : detectScorecardFields(prefillFromScorecard.recommendation, heroAsin.bulletPoints.length);
      setStep(2); setSelectedFields(scorecardFields); openTargetPhraseRef.current = initialTargetPhrase ?? ""; setTargetPhrase(initialTargetPhrase ?? ""); setStrategyLinkId(initialStrategyLinkId); setIntentFrom(heroAsin.declaredIntent.primary); setIntentTo("");
      setHypothesis(prefillFromScorecard.recommendation); setVariantBTitle(heroAsin.title); setVariantBBullets(mkBB());
      setVariantBMode("starting-point"); setLastSource("scorecard"); setLastSourceDetail(prefillFromScorecard.question);
      requestAnimationFrame(() => { skipPersistRef.current = false; }); return;
    }
    if (prefillFromSignal) {
      skipPersistRef.current = true;
      setStep(2); setSelectedFields(["title"]); openTargetPhraseRef.current = initialTargetPhrase ?? ""; setTargetPhrase(initialTargetPhrase ?? ""); setStrategyLinkId(initialStrategyLinkId); setIntentFrom(heroAsin.declaredIntent.primary); setIntentTo("");
      setHypothesis(prefillFromSignal.statement); setVariantBTitle(heroAsin.title); setVariantBBullets(mkBB());
      setVariantBMode("starting-point"); setLastSource("brand-signal"); setLastSourceDetail(prefillFromSignal.statement);
      requestAnimationFrame(() => { skipPersistRef.current = false; }); return;
    }
    if (prefillFromKeyword) {
      skipPersistRef.current = true;
      setStep(2); setSelectedFields(["title"]); openTargetPhraseRef.current = prefillFromKeyword.keyword; setTargetPhrase(prefillFromKeyword.keyword); setStrategyLinkId(initialStrategyLinkId); setIntentFrom(heroAsin.declaredIntent.primary); setIntentTo(""); setHypothesis("");
      setVariantBTitle(heroAsin.title); setVariantBBullets(mkBB());
      setVariantBMode("use-ai"); setLastSource(prefillFromKeyword.source); setLastSourceDetail(prefillFromKeyword.keyword);
      requestAnimationFrame(() => { skipPersistRef.current = false; }); return;
    }
    if (!initialTestType && !initialTargetPhrase) {
      const saved = readSessionDraft();
      if (saved && saved.heroAsinId === String(heroAsin.id)) {
        skipPersistRef.current = true;
        setStep(saved.step); setSelectedFields(saved.selectedFields); openTargetPhraseRef.current = saved.targetPhrase; setTargetPhrase(saved.targetPhrase);
        setStrategyLinkId("strategyLinkId" in saved ? saved.strategyLinkId : initialStrategyLinkId);
        setIntentFrom(saved.intentFrom); setIntentTo(saved.intentTo); setHypothesis(saved.hypothesis);
        setVariantBTitle(saved.variantBTitle || heroAsin.title);
        const rb = { ...saved.variantBBullets };
        heroAsin.bulletPoints.forEach((bp, i) => { const k = `bullet-${i + 1}`; if (!rb[k]) rb[k] = bp; });
        setVariantBBullets(rb); setVariantBMode(saved.variantBMode); setLastSource(saved.source ?? "manual"); setLastSourceDetail(saved.sourceDetail);
        setAppliedScorecardGaps(saved.appliedScorecardGaps ?? []);
        setGapSnapshots(saved.gapSnapshots ?? {});
        requestAnimationFrame(() => { skipPersistRef.current = false; }); return;
      }
    }
    const skipToStep2 = !!initialTestType && !!initialTargetPhrase;
    setStep(skipToStep2 ? 2 : 1); setSelectedFields(["title"]); openTargetPhraseRef.current = initialTargetPhrase ?? ""; setTargetPhrase(initialTargetPhrase ?? "");
    setStrategyLinkId(initialStrategyLinkId);
    setIntentFrom(heroAsin.declaredIntent.primary); setIntentTo(""); setHypothesis("");
    setVariantBTitle(heroAsin.title); setVariantBBullets(mkBB()); setVariantBMode("use-ai"); setLastSource("manual"); setLastSourceDetail("");
  }, [open, heroAsin.declaredIntent.primary, heroAsin.title, heroAsin.bulletPoints, initialTestType, initialTargetPhrase, initialStrategyLinkId, prefillFromDraft, prefillFromScorecard, prefillFromSignal, prefillFromKeyword]);
}
